/**
 * PressBoxShell — the header every Press Box page shares: the module name,
 * the team (switchable when the user keeps stats for more than one), and the
 * Games / Season / Settings tabs. 2026-10-17.
 */
import { useEffect, useState } from 'react';
import { NavLink, useNavigate, useOutletContext } from 'react-router-dom';
import { Radio, ListOrdered, Trophy, Settings, Users, Globe, BarChart3 } from 'lucide-react';
import { PRESS_BOX } from '../../constants/pressBox.js';
import api from '../../services/api.js';

/** 2026-10-22 (per Gilles) — the team's logo beside its name, when it has one. */
function TeamLogo({ teamId }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let alive = true; let obj = null;
    api.get(`/teams/${teamId}/press-box/logo`, { responseType: 'blob' })
      .then(({ data }) => { if (alive) { obj = URL.createObjectURL(data); setUrl(obj); } })
      .catch(() => {});
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [teamId]);
  if (!url) return null;
  return <img src={url} alt="" style={{ width: 28, height: 28, borderRadius: 7, objectFit: 'cover', border: '1px solid var(--border)' }} />;
}

export function keepsStats(team, userId) {
  if (!team) return false;
  if (['coach', 'manager'].includes(team.member_role)) return true;
  return Array.isArray(team.settings?.stat_recorders) && team.settings.stat_recorders.includes(userId);
}

export default function PressBoxShell({ teamId, userId, children, right = null }) {
  const navigate = useNavigate();
  const { teams = [] } = useOutletContext() || {};
  const mine = teams.filter(t => keepsStats(t, userId));
  const team = teams.find(t => String(t.id) === String(teamId));
  const tab = ({ isActive }) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 10, fontSize: 13, fontWeight: 700,
    textDecoration: 'none', color: isActive ? 'var(--ice)' : 'var(--text-2)', background: isActive ? 'var(--ice-a8)' : 'transparent',
  });
  return (
    <div className="animate-fade-in" style={{ maxWidth: 980, margin: '0 auto' }}>
      <div className="flex items-center justify-between gap-3 mb-1" style={{ flexWrap: 'wrap' }}>
        <div className="flex items-center gap-2">
          <Radio size={20} style={{ color: 'var(--gold)' }} />
          <h1 className="section-title text-2xl">{PRESS_BOX.NAME}</h1>
        </div>
        <div className="flex items-center gap-2">
          {mine.length > 1 && teamId && <TeamLogo teamId={teamId} />}
          {mine.length > 1 ? (
            <select className="input-field" style={{ width: 'auto', padding: '8px 12px' }} value={teamId || ''}
              onChange={(e) => navigate(`/press-box/${e.target.value}`)} aria-label="Team">
              {mine.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          ) : (team && <span className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--text-2)' }}><TeamLogo teamId={teamId} /> {team.name}</span>)}
          {right}
        </div>
      </div>
      <div className="text-xs mb-3" style={{ color: 'var(--text-3)' }}>{PRESS_BOX.TAGLINE}</div>
      {teamId && (
        <nav className="flex items-center gap-1 mb-4" style={{ borderBottom: '1px solid var(--border)', paddingBottom: 8, overflowX: 'auto' }}>
          <NavLink end to={`/press-box/${teamId}`} style={tab}><ListOrdered size={14} /> Games</NavLink>
          <NavLink to={`/press-box/${teamId}/stats`} style={tab}><BarChart3 size={14} /> Stats</NavLink>
          <NavLink to={`/press-box/${teamId}/publish`} style={tab}><Globe size={14} /> Publish</NavLink>
          <NavLink to={`/press-box/${teamId}/season`} style={tab}><Trophy size={14} /> Season</NavLink>
          {team?.team_type === 'press_box' && (
            <NavLink to={`/press-box/${teamId}/roster`} style={tab}><Users size={14} /> Roster</NavLink>
          )}
          <NavLink to={`/press-box/${teamId}/settings`} style={tab}><Settings size={14} /> Settings</NavLink>
        </nav>
      )}
      {children}
    </div>
  );
}
